import React from 'react';
import styled from 'styled-components';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import Stars from '@/app/common/components/Stars';
import type { ControlProps } from '../../../Form';
import { OPTIONS_SORT, getMultilistboxValue } from '../utils';
import getListboxContainer from './getListboxContainer';
import FilterContainer from '../FilterContainer';
import type { AccommodationCategory } from '../../../../utils/api/filtersAPIResponseTypes';
import type { CommonProps } from './CommonProps';
import { LI_HEIGHT } from '../../../../styles/constants';

const StarsStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  line-height: 24px;
  min-height: ${LI_HEIGHT};
  svg {
    margin-right: 0 !important;
    width: 16px !important;
    height: 16px !important;
  }
`;

export const getStarsOption = ({
  value,
  isWaiting,
  accommodationCategory,
  selected,
}: {
  value: number;
  isWaiting: boolean;
  accommodationCategory: AccommodationCategory | null;
  selected: boolean;
}) => ({
  value,
  label: (
    <StarsStyled>
      <Stars stars={value} />
      &nbsp;
    </StarsStyled>
  ),
  ...(accommodationCategory
    ? { addLabel: accommodationCategory?.[`${value}`] ? `${accommodationCategory[`${value}`]}` : '0' }
    : {}),
  disabled: isWaiting || (!selected && !!accommodationCategory && !accommodationCategory[`${value}`]) || undefined,
});

const STARS_VALUES = [5, 4, 3, 2, 1];

// 1-2 star hotels are being deactivated (MPH-760): searches may legitimately have none,
// so a zero-count option would only offer a selection with no results. A selected value
// is never hidden nor disabled — the user must still be able to unselect it.
// Mirrors createStarsFilters.tsx, which does the same for the new filters.
const STARS_VALUES_HIDDEN_WHEN_EMPTY = [2, 1];

const isStarsOptionHidden = ({
  value,
  accommodationCategory,
  selectedValues,
}: {
  value: number;
  accommodationCategory: AccommodationCategory | null;
  selectedValues: (number | string)[];
}): boolean =>
  STARS_VALUES_HIDDEN_WHEN_EMPTY.includes(value) &&
  !!accommodationCategory &&
  !accommodationCategory[`${value}`] &&
  !selectedValues.includes(value);

export const getStarsOptions = ({
  isWaiting,
  accommodationCategory,
  selectedValues,
}: {
  isWaiting: boolean;
  accommodationCategory: AccommodationCategory | null;
  selectedValues: (number | string)[];
}) =>
  STARS_VALUES.filter((value) => !isStarsOptionHidden({ value, accommodationCategory, selectedValues })).map((value) =>
    getStarsOption({ value, isWaiting, accommodationCategory, selected: selectedValues.includes(value) })
  );

type GetStarsField = {
  searchParams: SearchParams;
  t: TranslationFunction;
  commonProps: CommonProps;
};

const getStarsField = ({ t, searchParams, commonProps }: GetStarsField): ControlProps => {
  const { dynamicFiltersResponse, isWaiting } = commonProps;
  const accommodationCategory = dynamicFiltersResponse ? dynamicFiltersResponse.accommodationCategory : null;
  const value = getMultilistboxValue(searchParams[URLParameters.STARS], true);

  return getListboxContainer({
    fieldName: URLParameters.STARS,
    containerName: FilterContainer.StarsContainer,
    containerTitle: t('vacation.oss.filters.stars.title'),
    options: getStarsOptions({ isWaiting, accommodationCategory, selectedValues: value }),
    value,
    fieldClassName: '',
    optionsSort: OPTIONS_SORT.ALPHABET,
    ...commonProps,
  });
};

export default getStarsField;
